import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Dialog,
  getFilePreviewUrl,
  openDialog,
  showNotification,
} from 'platform/components';
import {Box, HStack, Space} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {useMemo} from 'react';

import {last} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useCreateDocumentMutation,
  useGetBusinessCaseQuery,
  useGetCheckoutOffsettingOfLiabilitiesAndReceivablesQuery,
} from '@dms/api/sales';
import {EntityResourceIds, FileResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {
  EMPTY_PLACEHOLDER,
  usePermissions,
  DocumentsDialog,
  useDocumentTemplates,
} from '@dms/shared';
import {PriceBox, SimpleTable, ColumnType} from '@dms/teas';

import {suffixTestId, TestIdProps} from 'shared/web';

type CreditDifferenceRow = {
  name: string;
  amountWithoutVat: string;
  vat: string;
  amount: string;
};

interface CheckoutCreditSettlementProps extends TestIdProps {
  checkoutId: string;
  orderId?: string;
  customerId?: string;
  isOpen: boolean;
  toggleOpen: () => void;
  businessCaseId: string;
}

export function CheckoutCreditSettlement({
  checkoutId,
  orderId,
  customerId,
  isOpen,
  toggleOpen,
  ...props
}: CheckoutCreditSettlementProps) {
  const formatCurrency = useFormatCurrency();

  const {
    data: offsettingOfLiabilitiesAndReceivables,
    isLoading: isLoadingLiabilitiesAndReceivables,
    isError: isErrorLiabilitiesAndReceivables,
  } = useGetCheckoutOffsettingOfLiabilitiesAndReceivablesQuery({checkoutId});

  const [getTemplatesByCode, {isLoading: isLoadingTemplates, isError: isTemplatesError}] =
    useDocumentTemplates();

  const {data: businessCaseParticipation} = useGetParticipationQuery({
    recordId: props.businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });

  const {data: businessCaseData} = useGetBusinessCaseQuery({businessCaseId: props.businessCaseId});

  const [canReadDocuments, canReadOffsettingOfLiabilitiesAndReceivablesDocument] = usePermissions({
    permissionKeys: ['readDocuments', 'readOffsettingOfLiabilitiesAndReceivablesDocument'],
    scopes: {
      readOffsettingOfLiabilitiesAndReceivablesDocument: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
    },
  });

  const isLoading = isLoadingLiabilitiesAndReceivables || isLoadingTemplates;
  const isError = isErrorLiabilitiesAndReceivables || isTemplatesError;

  const templateId = getTemplatesByCode('offsetting-of-liabilities-and-receivables').primaryTemplate
    ?.id;

  const liabilities = useMemo(
    () => [
      {
        data: offsettingOfLiabilitiesAndReceivables?.liabilities
          ? [
              ...offsettingOfLiabilitiesAndReceivables.liabilities.map((liability) => ({
                id: liability.id,
                name: liability.name,
                amountWithoutVat: liability.price.withoutVat
                  ? (formatCurrency(
                      parseFloat(liability.price.withoutVat.amount),
                      liability.price.withoutVat.currency
                    ) ?? EMPTY_PLACEHOLDER)
                  : EMPTY_PLACEHOLDER,
                vat: liability.price.vatRate ? `${liability.price.vatRate}%` : '–',
                amount:
                  formatCurrency(
                    parseFloat(liability.price.withVat.amount),
                    liability.price.withVat.currency
                  ) ?? EMPTY_PLACEHOLDER,
              })),
            ]
          : [],
      },
    ],
    [offsettingOfLiabilitiesAndReceivables?.liabilities, formatCurrency]
  );

  const claims = useMemo(
    () => [
      {
        data: offsettingOfLiabilitiesAndReceivables?.claim
          ? [
              ...offsettingOfLiabilitiesAndReceivables.claim.map((claim) => ({
                id: claim.id,
                name: claim.name,
                amountWithoutVat: claim.price.withoutVat
                  ? (formatCurrency(
                      parseFloat(claim.price.withoutVat.amount),
                      claim.price.withoutVat.currency
                    ) ?? EMPTY_PLACEHOLDER)
                  : EMPTY_PLACEHOLDER,
                vat: claim.price.vatRate ? `${claim.price.vatRate}%` : '–',
                amount:
                  formatCurrency(
                    parseFloat(claim.price.withVat.amount),
                    claim.price.withVat.currency
                  ) ?? EMPTY_PLACEHOLDER,
              })),
            ]
          : [],
      },
    ],
    [offsettingOfLiabilitiesAndReceivables?.claim, formatCurrency]
  );

  const [createCheckoutDocuments, {isLoading: isCreateDocumentMutationLoading}] =
    useCreateDocumentMutation();

  const createCheckoutOffsettingOfLiabilitiesAndReceivables = async () => {
    if (isNilOrEmpty(templateId)) {
      showNotification.error('general.notifications.noTemplateFound');
      return;
    }

    const checkout = await createCheckoutDocuments({
      checkoutId,
      orderId: orderId || '',
      templateIds: [templateId!],
    }).unwrap();

    const lastFile = last(checkout.orders[0]?.files);
    if (lastFile) {
      const document: FileResponseBody = {
        id: lastFile.fileFileId ?? lastFile.fileId,
        filename: lastFile.name,
        pdfFilename: lastFile.name,
        fileUri: lastFile.fileUri,
        pdfUri: getFilePreviewUrl(lastFile.fileUri),
      };

      openDialog(
        <DocumentsDialog
          documents={[document]}
          permissions={{canReadOffsettingOfLiabilitiesAndReceivablesDocument}}
          customerId={customerId}
          text={i18n.t('entity.checkout.labels.creditDifferenceDocumentsDialogNote')}
          isDocxFormat
        />,
        {
          title: i18n.t('entity.checkout.labels.creditDifferenceDocumentsDialog'),
          buttons: [
            {
              variant: 'secondary',
              title: i18n.t('general.actions.close'),
              onClick: closeCurrentDialog,
            },
          ],
        }
      );
    }

    toggleOpen();
  };

  return (
    <Dialog
      title={i18n.t('entity.checkout.labels.creditDifference')}
      data-testid={props['data-testid']}
      isOpen={isOpen}
      onClose={toggleOpen}
    >
      <Box minHeight={10}>
        <DataStatus isLoading={isLoading} isError={isError}>
          <>
            <SimpleTable
              data-testid={suffixTestId('liabilities', props)}
              noZebra
              columns={columns('liabilities')}
              rows={liabilities}
            />
            <SimpleTable
              data-testid={suffixTestId('claims', props)}
              noZebra
              columns={columns('claims')}
              rows={claims}
            />
            <HStack justify="flex-end">
              <Box paddingTop={2} paddingBottom={4}>
                <PriceBox
                  data-testid={suffixTestId('gap', props)}
                  align="right"
                  title={i18n.t('general.labels.gap')}
                  price={offsettingOfLiabilitiesAndReceivables?.credit}
                />
              </Box>
            </HStack>
          </>
        </DataStatus>
      </Box>

      <Space vertical={4} />
      <ButtonGroup align="right" data-testid={props['data-testid']}>
        <Button
          onClick={toggleOpen}
          data-testid={suffixTestId('cancel', props)}
          variant="secondary"
          title={i18n.t('general.actions.cancel')}
          isDisabled={isCreateDocumentMutationLoading}
        />
        <Button
          data-testid={suffixTestId('generateCreditDifference', props)}
          onClick={createCheckoutOffsettingOfLiabilitiesAndReceivables}
          isLoading={isCreateDocumentMutationLoading}
          isDisabled={!canReadDocuments}
          title={i18n.t('entity.checkout.labels.generateCreditDifference')}
        />
      </ButtonGroup>
    </Dialog>
  );
}

const columns = (name: string): Array<ColumnType<CreditDifferenceRow>> => [
  {id: 'name', width: 300, Header: i18n.t(`entity.checkout.labels.${name}`)},
  {
    id: 'amountWithoutVat',
    width: 150,
    align: 'right',
    Header: i18n.t('general.labels.amountWithoutVat'),
  },
  {id: 'vat', width: 100, align: 'right', Header: i18n.t('general.labels.vat')},
  {id: 'amount', align: 'right', Header: i18n.t('general.labels.amount')},
];
